import React, { FC } from 'react';

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import dynamic from 'next/dynamic';
import { ArrowSquareOut } from 'phosphor-react';
import {
  VestaIcon,
  VestaIconTypes,
  VestaNetworkIcons,
  VestaPartnerIcons,
} from 'src/v2/components/ui/icons/vesta';

import { NormalLink } from '~components/shared/link';
import { black, primaryColors } from '~components/theme/styles/colors';

import SlideWrapper from '../SlideWrapper';

const MobileFadingContainer = dynamic(
  () =>
    import('src/v2/components/ui/containers/mobile').then(
      ({ MobileFadingContainer }) => MobileFadingContainer,
    ),
  { ssr: false },
);

const PartnerLatticeNode: FC<{
  icon: VestaIconTypes;
  iconSize?: number;
  toolTipTitle: string;
  linkLabel: string;
  href: string;
}> = ({ icon, iconSize = 36, toolTipTitle, linkLabel, href }) => {
  return (
    <Tooltip
      placement="bottom"
      title={
        <Stack spacing={1} mt={0.5}>
          <Typography variant={'label3'} color={primaryColors[700]}>
            {toolTipTitle}
          </Typography>
          <NormalLink href={href} target={'_blank'} sx={{ textDecoration: 'none' }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography variant={'label3'}>{linkLabel}</Typography>
              <Box ml={1}>
                <ArrowSquareOut size={14} weight="bold" />
              </Box>
            </Stack>
          </NormalLink>
        </Stack>
      }
    >
      <Box>
        <VestaIcon
          sx={{
            boxShadow: `2px 1px 10px ${black['45%']}`,
            transition: 'border-color 0.15s linear',
            ': hover': { borderColor: primaryColors[50] },
          }}
          round
          icon={icon}
          size={iconSize}
          containerPadding={36}
        />
      </Box>
    </Tooltip>
  );
};

const PartnerWeaveSlide: FC = () => {
  return (
    <SlideWrapper>
      <Stack spacing={4}>
        <Typography variant={'h4'} color={primaryColors[50]}>
          Weaving Layer-2
        </Typography>
        <Typography variant={'body1'} color={primaryColors[400]}>
          VST is collateralized by a variety of collaterals. All collaterals onboarded go through a rigorous
          onboarding process and the respective risks are continuously monitored.
        </Typography>
      </Stack>

      <MobileFadingContainer
        app={
          <Stack px={'160px'} spacing={8} mt={14}>
            <Stack
              direction={'row'}
              spacing={'142px'}
              width={'100%'}
              alignItems={'center'}
              px={'120px'}
              justifyContent={'center'}
            >
              <PartnerLatticeNode
                icon={VestaPartnerIcons.OHM}
                linkLabel={'OLYMPUS DAO'}
                toolTipTitle={'DEEP INTEGRATION'}
                href={''}
              />
              <PartnerLatticeNode
                iconSize={56}
                icon={VestaNetworkIcons.ARBITRUM}
                linkLabel={'ARBITRUM'}
                toolTipTitle={'ECOSYSTEM'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.GMX}
                linkLabel={'GMX'}
                toolTipTitle={'DEEP INTEGRATION'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.OPENOCEAN}
                linkLabel={'OPENOCEAN'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.ONEINCH}
                linkLabel={'1INCH'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.BAL}
                linkLabel={'BALANCER'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.CURVE}
                linkLabel={'CURVE'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} px={'120px'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.REDACTED}
                linkLabel={'REDACTED CARTEL'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.AURA}
                linkLabel={'AURA'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.BPROTOCOL}
                linkLabel={'B.PROTOCOL'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.RISKDAO}
                linkLabel={'RISK DAO'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.SYNAPSE}
                linkLabel={'SYNAPSE'}
                toolTipTitle={'BRIDGING'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.LIFI}
                linkLabel={'LI.FI'}
                toolTipTitle={'BRIDGING'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.VERILOG}
                linkLabel={'VERILOG'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
            </Stack>
            <Box width={'100%'} display={'flex'} justifyContent={'center'} textAlign={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.CREDPROTOCOL}
                linkLabel={'CRED PROTOCOL'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
            </Box>
          </Stack>
        }
        mobile={
          <Stack px={'160px'} spacing={8} mt={14}>
            <Stack
              direction={'row'}
              spacing={'142px'}
              width={'100%'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <PartnerLatticeNode
                iconSize={56}
                icon={VestaNetworkIcons.ARBITRUM}
                linkLabel={'ARBITRUM'}
                toolTipTitle={'ECOSYSTEM'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.ONEINCH}
                linkLabel={'1INCH'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.BAL}
                linkLabel={'BALANCER'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.AURA}
                linkLabel={'AURA'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.SYNAPSE}
                linkLabel={'SYNAPSE'}
                toolTipTitle={'BRIDGING'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.LIFI}
                linkLabel={'LI.FI'}
                toolTipTitle={'BRIDGING'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.CURVE}
                linkLabel={'CURVE'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.RISKDAO}
                linkLabel={'RISK DAO'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.VERILOG}
                linkLabel={'VERILOG'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.CREDPROTOCOL}
                linkLabel={'CRED PROTOCOL'}
                toolTipTitle={'SECURITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.OHM}
                linkLabel={'OLYMPUS DAO'}
                toolTipTitle={'DEEP INTEGRATION'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.GMX}
                linkLabel={'GMX'}
                toolTipTitle={'DEEP INTEGRATION'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.OPENOCEAN}
                linkLabel={'OPENOCEAN'}
                toolTipTitle={'SWAP & TRADE'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}>
              <PartnerLatticeNode
                icon={VestaPartnerIcons.REDACTED}
                linkLabel={'REDACTED CARTEL'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
              <PartnerLatticeNode
                icon={VestaPartnerIcons.BPROTOCOL}
                linkLabel={'B.PROTOCOL'}
                toolTipTitle={'LIQUIDITY'}
                href={''}
              />
            </Stack>
            <Stack direction={'row'} spacing={'142px'} width={'100%'} justifyContent={'center'}></Stack>
            <Box width={'100%'} display={'flex'} justifyContent={'center'} textAlign={'center'}></Box>
          </Stack>
        }
      />
    </SlideWrapper>
  );
};

export default PartnerWeaveSlide;
